// ============================================
// Order Service (Client-side)
//
// HIPAA: All order access goes through Cloud Functions.
// Firestore rules deny direct client reads/writes to orders.
// This service wraps the Cloud Function calls.
// ============================================

import type { Order, OrderStatus } from '../../types/order';

import { CF_BASE_URL } from '../../config/api';

const BASE_URL = CF_BASE_URL;

// --- Patient-facing functions ---

export async function getOrderBySessionId(sessionId: string): Promise<Order | null> {
  const res = await fetch(`${BASE_URL}/getOrderStatus`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to retrieve order');

  const data = await res.json();
  return data.order as Order;
}

export async function getOrderById(orderId: string): Promise<Order | null> {
  const res = await fetch(`${BASE_URL}/getOrderById`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId }),
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to retrieve order');

  const data = await res.json();
  return data.order as Order;
}

// --- Vendor-facing functions (require auth token) ---

function getVendorToken(): string {
  const token = sessionStorage.getItem('vendor_token');
  if (!token) throw new Error('Vendor not authenticated');
  return token;
}

export async function vendorAuthenticate(password: string): Promise<string> {
  const res = await fetch(`${BASE_URL}/vendorAuth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (res.status === 401) throw new Error('Invalid password');
  if (!res.ok) throw new Error('Authentication failed');

  const data = await res.json();
  sessionStorage.setItem('vendor_token', data.token);
  return data.token;
}

export async function getAllOrders(): Promise<Order[]> {
  const token = getVendorToken();
  const res = await fetch(`${BASE_URL}/vendorGetOrders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (res.status === 401) {
    sessionStorage.removeItem('vendor_token');
    throw new Error('Vendor session expired');
  }
  if (!res.ok) throw new Error('Failed to retrieve orders');

  const data = await res.json();
  return data.orders as Order[];
}

export async function getOrdersByStatus(status: OrderStatus): Promise<Order[]> {
  const token = getVendorToken();
  const res = await fetch(`${BASE_URL}/vendorGetOrders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  if (res.status === 401) {
    sessionStorage.removeItem('vendor_token');
    throw new Error('Vendor session expired');
  }
  if (!res.ok) throw new Error('Failed to retrieve orders');

  const data = await res.json();
  return data.orders as Order[];
}

export async function getOrder(id: string): Promise<Order | null> {
  // Vendor uses getAllOrders and filters — or we could add a
  // dedicated function. For now, use vendorGetOrders.
  const orders = await getAllOrders();
  return orders.find((o) => o.id === id) ?? null;
}

export async function updateOrderStatus(
  id: string,
  status: OrderStatus,
  extra?: { trackingNumber?: string; trackingCarrier?: string; estimatedDelivery?: string; vendorNotes?: string },
): Promise<void> {
  const token = getVendorToken();
  const res = await fetch(`${BASE_URL}/vendorUpdateOrder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      orderId: id,
      status,
      ...extra,
    }),
  });

  if (res.status === 401) {
    sessionStorage.removeItem('vendor_token');
    throw new Error('Vendor session expired');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to update order');
  }
}

export async function updateTracking(
  id: string,
  trackingNumber: string,
  trackingCarrier: string,
): Promise<void> {
  await updateOrderStatus(id, 'shipped', { trackingNumber, trackingCarrier });
}

// --- Admin: seed test orders ---

export async function seedTestOrders(
  orders: Record<string, unknown>[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  onProgress?.(`Sending ${orders.length} test orders to Cloud Function...`);

  const res = await fetch(`${BASE_URL}/seedTestOrders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      orders,
      seedKey: 'hemologix-seed-2025',
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to seed orders');
  }

  const data = await res.json();
  onProgress?.(`Successfully wrote ${data.written} orders to Firestore`);
  return data.written;
}

// --- Removed: getOrdersByEmail ---
// HIPAA: Email-based lookup removed. Patients look up orders by order ID only.
// The order ID is a secret known only to the payer (shown on success page).
